import { useEffect, useState } from 'react';

import type { HostPlayP2pClient } from '@genially/p2p-client';
import { HostPlayP2pClientEventType } from '@genially/p2p-client';

/**
 * Who is in the room right now, as a list this panel can recompute against.
 *
 * `client.players` is derived from the connection map, which is a plain `Map` rather than
 * an observable, so no amount of `observer` would notice somebody arriving. The client
 * does announce it, though, so the panel listens and re-reads.
 */
export const useConnectedUsernames = (client: HostPlayP2pClient): string[] => {
  const [usernames, setUsernames] = useState<string[]>(() =>
    client.players.map(({ username }) => username),
  );

  useEffect(() => {
    const refresh = () => {
      setUsernames(client.players.map(({ username }) => username));
    };

    // Joins and departures each arrive as a single or a batched event depending on how
    // many happened at once, so all four are needed to keep the count honest. A rename
    // changes no membership but does change what this list should show for that player —
    // without listening for it too, a claimed alias never replaces the placeholder here.
    const events = [
      HostPlayP2pClientEventType.PlayerJoined,
      HostPlayP2pClientEventType.PlayersJoined,
      HostPlayP2pClientEventType.PlayerDisconnected,
      HostPlayP2pClientEventType.PlayersDisconnected,
      HostPlayP2pClientEventType.PlayerRenamed,
    ] as const;

    events.forEach(event => client.on(event, refresh));
    refresh();

    return () => {
      events.forEach(event => client.off(event, refresh));
    };
  }, [client]);

  return usernames;
};
